/**
 * Подсказка «открыто / закрывается / снаружи» по строке часов и IANA-зоне.
 * Синк с `frontend/admin/src/shared/lib/branch-hours-status.ts` (менять в двух местах).
 */

export type BranchHoursLiveHint = { kind: 'open' } | { kind: 'closes_in'; minutes: number } | null;

const RANGE_RE = /(\d{1,2}):(\d{2})\s*[–—-]\s*(\d{1,2}):(\d{2})/;

export function getMinutesSinceMidnightInTimeZone(date: Date, timeZone: string): number | null {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    });
    const parts = fmt.formatToParts(date);
    let hour = 0;
    let minute = 0;
    for (const p of parts) {
      if (p.type === 'hour') hour = parseInt(p.value, 10);
      if (p.type === 'minute') minute = parseInt(p.value, 10);
    }
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    return hour * 60 + minute;
  } catch {
    return null;
  }
}

export function parseFirstDailyTimeRangeMinutes(workingHoursRaw: string | null | undefined): {
  open: number;
  close: number;
} | null {
  const s = (workingHoursRaw ?? '').trim();
  if (!s) return null;
  const m = s.match(RANGE_RE);
  if (!m) return null;
  const h1 = parseInt(m[1] ?? '', 10);
  const mi1 = parseInt(m[2] ?? '', 10);
  const h2 = parseInt(m[3] ?? '', 10);
  const mi2 = parseInt(m[4] ?? '', 10);
  if ([h1, mi1, h2, mi2].some((x) => !Number.isFinite(x))) return null;
  if (mi1 > 59 || mi2 > 59) return null;
  const open = h1 * 60 + mi1;
  const close = h2 * 60 + mi2;
  if (close <= open) return null;
  return { open, close };
}

export function branchHoursLiveHint(
  workingHoursRaw: string | null | undefined,
  timeZone: string,
  now: Date = new Date()
): BranchHoursLiveHint {
  const range = parseFirstDailyTimeRangeMinutes(workingHoursRaw);
  if (!range) return null;
  const mins = getMinutesSinceMidnightInTimeZone(now, timeZone);
  if (mins == null) return null;
  if (mins < range.open || mins >= range.close) return null;
  const untilClose = range.close - mins;
  if (untilClose > 0 && untilClose < 60) {
    return { kind: 'closes_in', minutes: untilClose };
  }
  return { kind: 'open' };
}

const EN_WEEKDAY_TO_API: Record<string, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6
};

function getApiWeekdayInTimeZone(date: Date, timeZone: string): number {
  const long = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' }).format(date);
  return EN_WEEKDAY_TO_API[long.toLowerCase()] ?? 0;
}

function normalizeWorkingWeekdays(w: number[] | null | undefined): number[] {
  if (w == null || w.length === 0) return [];
  return [...new Set(w.filter((d) => d >= 0 && d <= 6))].sort((a, b) => a - b);
}

/**
 * Минуты от `now` до ближайшего «открытия» (первый интервал вида 10:00–20:00) по графику филиала.
 * Включая случай, когда **сегодня не рабочий** по `workingWeekdays` — ближайшее открытие в следующем рабочем дне.
 * `null` — сейчас внутри окна «открыто», нет парсинга часов, не заданы дни/интервал.
 */
export function minutesUntilNextBranchOpen(
  workingHoursRaw: string | null | undefined,
  timeZone: string,
  workingWeekdays: number[] | null | undefined,
  now: Date = new Date()
): number | null {
  const range = parseFirstDailyTimeRangeMinutes(workingHoursRaw);
  if (!range) return null;
  const wdays = normalizeWorkingWeekdays(workingWeekdays);
  if (wdays.length === 0) return null;
  const mins = getMinutesSinceMidnightInTimeZone(now, timeZone);
  if (mins == null) return null;
  const todayDow = getApiWeekdayInTimeZone(now, timeZone);

  const walkNextOpen = (fromDow: number, fromElapsedBase: number): number | null => {
    let elapsed = fromElapsedBase;
    let dow = fromDow % 7;
    for (let k = 0; k < 7; k += 1) {
      if (wdays.includes(dow)) {
        return elapsed + range.open;
      }
      elapsed += 1440;
      dow = (dow + 1) % 7;
    }
    return null;
  };

  if (!wdays.includes(todayDow)) {
    return walkNextOpen((todayDow + 1) % 7, 1440 - mins);
  }
  if (mins < range.open) {
    return range.open - mins;
  }
  if (mins < range.close) {
    return null;
  }
  return walkNextOpen((todayDow + 1) % 7, 1440 - mins);
}
